import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import MyAccount from '@/src/account/components/MyAccount';
import UpdateProfileForm from '@/src/account/components/UpdateProfile';
import Container from '@/src/shared/components/Container';
import Layout from '@/src/shared/components/Layout';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import withAuthentication from '@/src/shared/components/WithAuthentication';
import { getProfileDetails } from '@/src/updateProfile/redux/actions';
import request from '@/utils/axios';

const UpdateProfile = props => {
  const {
    personId,
    optionsData: { Prefixes, Countries, States },
    updateProfile,
    roles,
  } = props;

  async function loadProfileDetails(id) {
    if (!id) return;
    try {
      await props.getProfileDetails(id);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    loadProfileDetails(personId);
  }, [personId]);

  const myProfileFormFields = {
    aboutMe: {
      firstName: {
        label: 'First Name',
        required: true,
        persist: true,
        type: 'input',
        field: 'First',
        formOverrides: {
          rules: [{ required: true, message: 'Enter First Name.' }],
        },
      },
      middleName: {
        label: 'Middle Initial',
        required: true,
        persist: true,
        type: 'input',
        field: 'Middle',
      },
      lastName: {
        label: 'Last Name',
        required: true,
        persist: true,
        type: 'input',
        field: 'Last',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Last Name.' }],
        },
      },
      prefix: {
        label: 'Prefix',
        required: false,
        persist: true,
        type: 'dropdown',
        options: Prefixes,
        optionId: 'PrefixId',
        optionDesc: 'PrefixDescription',
        field: 'Prefix',
      },
      suffix: {
        label: 'Suffix',
        required: false,
        persist: true,
        type: 'input',
        field: 'Suffix',
      },
      informalName: {
        label: 'Informal Name',
        required: false,
        persist: true,
        type: 'input',
        field: 'Informal',
      },
      title: {
        label: 'Title',
        required: true,
        persist: true,
        type: 'input',
        field: 'Title',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Title.' }],
        },
      },
      company: {
        label: 'Company',
        required: true,
        persist: true,
        type: 'input',
        field: 'Company',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Company.' }],
        },
      },
    },
    contactInformation: {
      emailAddress: {
        label: 'Email',
        required: true,
        persist: true,
        type: 'inputEmail',
        field: 'EmailAddress',
        inputType: 'email',
      },
      country: {
        label: 'Country',
        required: true,
        persist: true,
        type: 'inputCountry',
        field: 'CountryCode',
        options: Countries,
        optionId: 'CountryCode',
        optionDesc: 'CountryDescription',
        formOverrides: {
          rules: [{ required: true, message: 'Select Country.' }],
        },
        extra:
          "Note: If your country does not appear in the list, select 'Other' and enter the country name in the box that appears below.",
      },
      officeAddress: {
        label: 'Corporate Address',
        required: true,
        persist: true,
        type: 'input',
        field: 'StreetAddress1',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Office Address.' }],
        },
        extra: 
          'Note: No home addresses.'
      },
      suite: {
        label: 'Suite',
        required: false,
        persist: true,
        type: 'input',
        field: 'StreetAddress2',
      },
      city: {
        label: 'City',
        required: true,
        persist: true,
        type: 'input',
        field: 'City',
        formOverrides: {
          rules: [{ required: true, message: 'Enter City.' }],
        },
      },
      state: {
        label: 'State',
        required: true,
        persist: true,
        type: 'inputOfficeState',
        field: 'StateCode',
        options: States,
        optionId: 'StateCode',
        optionDesc: 'StateDescription',
        formOverrides: {
          rules: [{ required: true, message: 'Select State.' }],
        },
      },
      postalCode: {
        label: 'Postal Code',
        required: true,
        persist: true,
        type: 'input',
        field: 'PostalCode',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Postal Code' }],
        },
      },
      phoneNumber: {
        label: 'Phone',
        required: true,
        persist: true,
        type: 'input',
        field: 'Number',
        formOverrides: {
          rules: [
            {
              required: true,
              pattern: new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
              message: 'Enter Phone Number.',
            },
          ],
        },
      },
      extension: {
        label: 'Extension',
        required: false,
        persist: true,
        type: 'input',
        field: 'Extension',
      },
      mobile: {
        label: 'Mobile',
        required: false,
        persist: true,
        type: 'input',
        field: 'Mobile',
        formOverrides: {
          rules: [
            {
              required: false,
              pattern: new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
              message: 'Enter A Valid Mobile.',
            },
          ],
        },
      },
    },
    mailingInformation: {
      extraAddress: {
        label: 'Mailing Address',
        checkboxLabel: 'Same as Office Address',
        required: false,
        persist: false,
        type: 'checkbox',
      },
      poBoxNumber: {
        label: 'P.O. Box Number',
        meta: 'extra',
        required: true,
        persist: true,
        type: 'input',
        field: 'PoBoxNumber',
        isMailing: true,
        formOverrides: {
          rules: [{ required: true, message: 'Enter P.O. Box Number.' }],
        },
      },
      altCity: {
        label: 'City',
        meta: 'extra',
        required: true,
        persist: true,
        type: 'input',
        field: 'PoBoxCity',
        isMailing: true,
        formOverrides: {
          rules: [{ required: true, message: 'Enter City.' }],
        },
      },
      altState: {
        label: 'State',
        required: true,
        persist: true,
        type: 'inputMailingState',
        field: 'PoBoxStateCode',
        options: States,
        optionId: 'StateCode',
        optionDesc: 'StateDescription',
        isMailing: true,
        formOverrides: {
          rules: [{ required: true, message: 'Select State.' }],
        },
      },
      altPostalCode: {
        label: 'Postal Code',
        meta: 'extra',
        required: true,
        persist: true,
        type: 'input',
        field: 'PoBoxPostalCode',
        isMailing: true,
        formOverrides: {
          rules: [{ required: true, message: 'Enter Postal Code.' }],
        },
      },
    },
  };

  return (
    <Layout>
      <Section bgColor="gray" halfPad>
        <Container>
          <MyAccount
            mainContent={
              <UpdateProfileForm
                fieldInfo={myProfileFormFields}
                updateProfile={updateProfile}
                roles={roles}
              />
            }
          />
          <WebLogImage path="/account/update-profile" title="Update My Profile" />
        </Container>
      </Section>
    </Layout>
  );
};

export async function getStaticProps() {
  try {
    const response = await request({
      url: 'profile/registrationoptions',
    });
    if (response.data && response.data.FieldOptions) {
      return {
        props: {
          optionsData: response.data.FieldOptions,
        },
      };
    }
    return {
      props: {
        optionsData: {
          Prefixes: [],
          Countries: [],
          States: [],
          SecurityQuestions: [],
        },
      },
    };
  } catch (err) {
    console.warn(err);
    return {
      props: {
        optionsData: {
          Prefixes: [],
          Countries: [],
          States: [],
          SecurityQuestions: [],
        },
      },
    };
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      userinfo: { PersonID },
      role,
    },
    updateProfile,
  } = state;
  return {
    personId: PersonID,
    updateProfile,
    roles: role,
  };
}

export default connect(
  mapStateToProps,
  {
    getProfileDetails,
  }
)(withAuthentication(UpdateProfile));
